import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import {
  FaIdCard,
  FaFingerprint,
  FaLock,
  FaCheckCircle,
  FaCheck,
  FaArrowRight,
  FaShieldAlt,
  FaHome,
  FaPhoneAlt,
  FaChevronDown,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import applyNowImage from '../assets/images/apply-new-side.jpg';
import { getFallbackCities } from '../utils/stateCityMapping';
import {
  buildAadhaarAddress,
  checkExistingCustomerByPan,
  checkMultiVerticalPan,
  checkPanBlacklist,
  clearPanCache,
  getCities,
  getPanCache,
  getPincodesByCityId,
  getStates,
  isAadhaarSendSuccess,
  isAadhaarVerifySuccess,
  isLeadSaveSuccess,
  evaluateMultiVerticalPan,
  isPanVerifySuccess,
  saveLendingPage,
  sendAadhaarOtp,
  setPanCache,
  verifyAadhaarOtp,
  verifyPan,
} from '../config/taskApi';
import './apply-new.css';

const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const MIN_LOAN_AMOUNT = 10000;
const MAX_LOAN_AMOUNT = 100000;
const MIN_MONTHLY_INCOME = 50000;
const MAX_MONTHLY_INCOME = 5000000;
const MAX_OBLIGATIONS = 5000000;
const MIN_ADDRESS_LENGTH = 15;
const MAX_ADDRESS_LENGTH = 500;
const OTP_RESEND_SECONDS = 60;

const emptyProfile = {
  firstName: '',
  middleName: '',
  lastName: '',
  dob: '',
  gender: '',
  addressFromKyc: '',
  maskedAadhaar: '',
  panName: '',
};

function splitFullName(fullName = '') {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', middleName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], middleName: '', lastName: '' };
  if (parts.length === 2) {
    return { firstName: parts[0], middleName: '', lastName: parts[1] };
  }
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}

function formatAadhaar(digits) {
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function SearchableSelect({
  label,
  required,
  value,
  displayValue,
  options,
  placeholder,
  disabled,
  error,
  onSelect,
  getOptionLabel,
  getOptionValue,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = options.filter((opt) =>
    String(getOptionLabel(opt) || '')
      .toLowerCase()
      .includes(query.toLowerCase())
  );

  return (
    <div className="apn-field" ref={wrapRef}>
      <label>
        {label} {required ? <em>*</em> : null}
      </label>
      <div className={`apn-search-select ${error ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''}`}>
        <input
          type="text"
          disabled={disabled}
          placeholder={placeholder}
          value={open ? query : displayValue || ''}
          onFocus={() => {
            if (disabled) return;
            setOpen(true);
            setQuery('');
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          autoComplete="off"
        />
        <FaChevronDown className={`apn-dropdown-ico ${open ? 'is-open' : ''}`} />
      </div>
      {open && !disabled && (
        <div className="apn-search-menu">
          {filtered.length === 0 && <div className="apn-search-empty">No results</div>}
          {filtered.map((opt) => {
            const id = getOptionValue(opt);
            return (
              <button
                type="button"
                key={id}
                className={String(id) === String(value) ? 'active' : ''}
                onClick={() => {
                  onSelect(opt);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {getOptionLabel(opt)}
              </button>
            );
          })}
        </div>
      )}
      {error ? <div className="apn-error">{error}</div> : null}
    </div>
  );
}

const ApplyNew = () => {
  const [panNumber, setPanNumber] = useState('');
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpReferenceId, setOtpReferenceId] = useState('');
  const [resendLeft, setResendLeft] = useState(0);
  const [panBlacklisted, setPanBlacklisted] = useState(false);
  const [panBlocked, setPanBlocked] = useState(false);
  const [panWarning, setPanWarning] = useState('');
  const [panVerifiedOk, setPanVerifiedOk] = useState(false);
  const [panChecking, setPanChecking] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [profile, setProfile] = useState(emptyProfile);
  const [panVerifyPayload, setPanVerifyPayload] = useState(null);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [stateId, setStateId] = useState('');
  const [cityId, setCityId] = useState('');
  const [form, setForm] = useState({
    monthlyIncome: '',
    loanAmount: '',
    mobile: '',
    alternateMobile: '',
    email: '',
    officialEmail: '',
    obligations: '',
    state: '',
    city: '',
    pincode: '',
    address: '',
    notificationConsent: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refId, setRefId] = useState('');
  const submitLockRef = useRef(false);

  useEffect(() => {
    getStates()
      .then((data) => {
        if (data?.Status === 1 && Array.isArray(data.Data)) setStates(data.Data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (resendLeft <= 0) return undefined;
    const t = setInterval(() => setResendLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendLeft]);

  const runPanChecks = useCallback(async (pan) => {
    if (!PAN_PATTERN.test(pan)) return;
    setPanChecking(true);
    setPanWarning('');
    setPanBlacklisted(false);
    setPanBlocked(false);
    setErrors((e) => ({ ...e, panNumber: '' }));

    try {
      // 1) FIRST: multi-vertical DISBURSED check (4 verticals)
      // On network error, continue — existing-customer API still decides status.
      let multiRes = null;
      try {
        multiRes = await checkMultiVerticalPan(pan);
      } catch {
        multiRes = null;
      }

      const multiCheck = evaluateMultiVerticalPan(multiRes || {});
      if (!multiCheck.allowed) {
        setPanBlocked(true);
        setPanVerifiedOk(false);
        setErrors((e) => ({
          ...e,
          panNumber: multiCheck.message,
        }));
        setPanChecking(false);
        return;
      }

      // 2) Then other 3 APIs in parallel
      const cached = getPanCache(pan);
      const [blacklistRes, existingRes, verifyRes] = await Promise.all([
        checkPanBlacklist(pan),
        checkExistingCustomerByPan(pan),
        cached
          ? Promise.resolve(cached)
          : verifyPan(pan).catch((err) => ({
              responseCode: 'ERR',
              responseMessage:
                err?.response?.data?.message ||
                err?.message ||
                'PAN verification failed',
              data: null,
              raw: err?.response?.data,
            })),
      ]);

      if (blacklistRes?.blacklisted === true) {
        setPanBlacklisted(true);
        setPanBlocked(true);
        setPanVerifiedOk(false);
        setErrors((e) => ({
          ...e,
          panNumber:
            blacklistRes.message ||
            blacklistRes.Message ||
            'This PAN is blacklisted. Please contact customer support.',
        }));
        setPanWarning('');
        setPanChecking(false);
        return;
      }

      // Only Reject / Close / Fresh can apply; any other lead status → block
      if (existingRes?.status === true) {
        const d = existingRes.data || {};
        const status = String(
          d.status || d.lead_status || d.leadStatus || ''
        )
          .toUpperCase()
          .trim();
        const allowedStatuses = [
          'CLOSED',
          'CLOSE',
          'REJECT',
          'REJECTED',
          'FRESH',
        ];
        if (!allowedStatuses.includes(status)) {
          setPanBlocked(true);
          setPanVerifiedOk(false);
          setErrors((e) => ({
            ...e,
            panNumber: `Your loan status is ${status || d.status || 'ACTIVE'}. Cannot re-apply.`,
          }));
          setPanWarning('');
          setPanChecking(false);
          return;
        }
        setPanWarning((prev) =>
          prev || `Latest Status: ${d.status || status} (You can re-apply)`
        );
      } else {
        // Fresh PAN (customer not found / no existing lead) — success note
        setPanWarning(
          'PAN verified successfully. You can continue your application.'
        );
      }

      const normalized = cached || verifyRes;
      if (!cached && isPanVerifySuccess(normalized)) {
        setPanCache(pan, normalized);
      }

      setPanVerifyPayload(normalized);

      if (isPanVerifySuccess(normalized)) {
        setPanVerifiedOk(true);
        const d = normalized.data || {};
        const nameParts = splitFullName(
          d.fullName || d.name || d.full_name || [d.firstName, d.lastName].filter(Boolean).join(' ')
        );
        setProfile((prev) => ({
          ...prev,
          ...nameParts,
          dob: d.dob || d.dateOfBirth || prev.dob,
          gender: String(d.gender || prev.gender || '').toUpperCase(),
          addressFromKyc: d.address || prev.addressFromKyc,
          maskedAadhaar: d.maskedAadhaarNumber || d.masked_aadhaar || prev.maskedAadhaar,
          panName: d.fullName || d.name || prev.panName,
        }));
        if (d.address && !form.address) {
          setForm((f) => ({ ...f, address: String(d.address).slice(0, MAX_ADDRESS_LENGTH) }));
        }
      } else {
        setPanVerifiedOk(false);
        const apiMsg = normalized.responseMessage || normalized.message || existingRes?.message || '';
        let msg = 'Government PAN verification failed. You may continue with Aadhaar OTP.';
        
        if (
          apiMsg.includes('PAN not found in any database') || 
          apiMsg.toLowerCase().includes('not found') ||
          apiMsg.includes('Customer not found')
        ) {
          msg = 'PAN does not exist. Enter your correct PAN.';
        } else if (apiMsg) {
          msg = apiMsg;
        }

        setPanWarning((prev) => prev || msg);
      }
      
    } catch (err) {
      setPanVerifiedOk(false);
      setErrors((e) => ({
        ...e,
        panNumber:
          err?.response?.data?.message ||
          err?.message ||
          'PAN check failed. Please try again.',
      }));
    } finally {
      setPanChecking(false);
    }
  }, [form.address]);

  const handlePanBlur = () => {
    if (PAN_PATTERN.test(panNumber)) runPanChecks(panNumber);
  };

  const handleSendOtp = async () => {
    const next = {};
    if (!PAN_PATTERN.test(panNumber)) next.panNumber = 'Enter a valid PAN (ABCDE1234F)';
    if (aadhaarNumber.length !== 12) next.aadhaarNumber = 'Enter a valid 12-digit Aadhaar';
    if (panBlacklisted) next.panNumber = 'Customer is blacklisted';
    if (panBlocked && !panWarning.includes('re-apply')) {
      next.panNumber = errors.panNumber || 'Cannot proceed with this PAN';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    setOtpBusy(true);
    try {
      const res = await sendAadhaarOtp(aadhaarNumber);
      if (isAadhaarSendSuccess(res)) {
        setOtpSent(true);
        setOtpReferenceId(res.referenceId || '');
        setResendLeft(OTP_RESEND_SECONDS);
        setErrors((e) => ({ ...e, otp: '' }));
      } else {
        setErrors((e) => ({
          ...e,
          aadhaarNumber: res.responseMessage || 'Failed to send OTP',
        }));
      }
    } catch {
      setErrors((e) => ({ ...e, aadhaarNumber: 'Failed to send OTP. Try again.' }));
    } finally {
      setOtpBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) {
      setErrors((e) => ({ ...e, otp: 'Enter a valid 6-digit OTP' }));
      return;
    }
    setOtpBusy(true);
    try {
      const res = await verifyAadhaarOtp(aadhaarNumber, otp, otpReferenceId);
      if (isAadhaarVerifySuccess(res)) {
        setOtpVerified(true);
        setErrors((e) => ({ ...e, otp: '' }));
        const d = res.data || {};
        const nameParts = splitFullName(d.name || d.fullName || '');
        const address = buildAadhaarAddress(d) || d.address || '';
        setProfile((prev) => ({
          ...prev,
          firstName: nameParts.firstName || prev.firstName,
          middleName: nameParts.middleName || prev.middleName,
          lastName: nameParts.lastName || prev.lastName,
          dob: d.dateOfBirth || d.dob || prev.dob,
          gender: String(d.gender || prev.gender || '').toUpperCase(),
          addressFromKyc: address || prev.addressFromKyc,
          maskedAadhaar:
            d.maskAadhaarNumber || d.maskedAadhaarNumber || prev.maskedAadhaar,
        }));
        if (address) {
          setForm((f) => ({
            ...f,
            address: f.address || String(address).slice(0, MAX_ADDRESS_LENGTH),
            pincode: f.pincode || d.pincode || '',
            state: f.state || d.state || '',
          }));
        }
      } else {
        setErrors((e) => ({
          ...e,
          otp: res.responseMessage || 'Invalid OTP',
        }));
      }
    } catch {
      setErrors((e) => ({ ...e, otp: 'OTP verification failed' }));
    } finally {
      setOtpBusy(false);
    }
  };

  const updateFormField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((er) => (er[field] ? { ...er, [field]: '' } : er));
  };

  const handleStateSelect = async (opt) => {
    const sid = String(opt.state_id);
    const name = opt.state;
    setStateId(sid);
    setCityId('');
    setCities([]);
    setPincodes([]);
    setForm((f) => ({ ...f, state: name, city: '', pincode: '' }));
    setErrors((e) => ({ ...e, state: '', city: '', pincode: '' }));

    try {
      const resp = await getCities(sid);
      if (resp?.Status === 1 && Array.isArray(resp.Data) && resp.Data.length) {
        setCities(resp.Data);
      } else {
        setCities(getFallbackCities(name));
      }
    } catch {
      setCities(getFallbackCities(name));
    }
  };

  const handleCitySelect = async (opt) => {
    const cid = String(opt.city_id);
    setCityId(cid);
    setPincodes([]);
    setForm((f) => ({ ...f, city: opt.city, pincode: '' }));
    setErrors((e) => ({ ...e, city: '', pincode: '' }));

    if (String(cid).startsWith('local-')) return;

    try {
      const resp = await getPincodesByCityId(cid);
      if (resp?.Status === 1 && Array.isArray(resp.Data)) {
        setPincodes(resp.Data);
      }
    } catch {
      setPincodes([]);
    }
  };

  const validateStep2 = () => {
    const next = {};
    const income = Number(form.monthlyIncome);
    const loan = Number(form.loanAmount);
    const obligations = form.obligations === '' ? 0 : Number(form.obligations);

    if (!income || income < MIN_MONTHLY_INCOME || income > MAX_MONTHLY_INCOME) {
      next.monthlyIncome = `Income must be ₹${MIN_MONTHLY_INCOME.toLocaleString('en-IN')} – ₹${MAX_MONTHLY_INCOME.toLocaleString('en-IN')}`;
    }
    if (!loan || loan < MIN_LOAN_AMOUNT || loan > MAX_LOAN_AMOUNT) {
      next.loanAmount = `Loan must be ₹${MIN_LOAN_AMOUNT.toLocaleString('en-IN')} – ₹${MAX_LOAN_AMOUNT.toLocaleString('en-IN')}`;
    } else if (income && loan > income * 5) {
      next.loanAmount = 'Loan amount cannot exceed 5× monthly income';
    }
    if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      next.mobile = 'Enter valid 10-digit Indian mobile';
    }
    if (form.alternateMobile) {
      if (!/^[6-9]\d{9}$/.test(form.alternateMobile)) {
        next.alternateMobile = 'Enter valid alternate mobile';
      } else if (form.alternateMobile === form.mobile) {
        next.alternateMobile = 'Alternate must differ from mobile';
      }
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = 'Enter valid personal email';
    }
    if (form.officialEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.officialEmail)) {
      next.officialEmail = 'Enter valid official email';
    }
    if (form.obligations !== '') {
      if (Number.isNaN(obligations) || obligations < 0 || obligations > MAX_OBLIGATIONS) {
        next.obligations = 'Invalid obligations amount';
      } else if (income && obligations > income) {
        next.obligations = 'Obligations cannot exceed monthly income';
      }
    }
    if (!stateId || !form.state) next.state = 'Select state';
    if (!form.city) next.city = 'Select city';
    if (!form.pincode || !/^\d{6}$/.test(form.pincode)) next.pincode = 'Select / enter 6-digit pincode';
    if (!form.address || form.address.trim().length < MIN_ADDRESS_LENGTH) {
      next.address = `Address must be at least ${MIN_ADDRESS_LENGTH} characters`;
    } else if (form.address.length > MAX_ADDRESS_LENGTH) {
      next.address = `Address max ${MAX_ADDRESS_LENGTH} characters`;
    }
    if (!form.notificationConsent) {
      next.notificationConsent = 'Please authorise notifications to continue';
    }
    if (panBlacklisted) next.panNumber = 'Customer is blacklisted';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Sync lock FIRST — double-click / Enter key se second hit block
    if (submitLockRef.current || submitting || submitted) return;
    submitLockRef.current = true;

    const next = validateStep2();
    setErrors(next);
    if (Object.keys(next).length) {
      submitLockRef.current = false;
      return;
    }

    setSubmitting(true);

    const cached = getPanCache(panNumber) || panVerifyPayload;
    const panData = cached?.data || {};
    const panOk = cached && isPanVerifySuccess(cached);

    const payload = {
      loan_amount: form.loanAmount,
      monthly_income: form.monthlyIncome,
      first_name: profile.firstName || 'NA',
      middle_name: profile.middleName || null,
      sur_name: profile.lastName || null,
      pan: panNumber,
      pan_status: panOk ? 1 : 0,
      pan_response_code: cached?.responseCode || null,
      pan_message: cached?.responseMessage || null,
      pan_reference_id: cached?.referenceId || null,
      pan_name: panData.fullName || panData.name || profile.panName || null,
      pan_address: panData.address || profile.addressFromKyc || null,
      pan_gender: panData.gender || profile.gender || null,
      pan_dob: panData.dob || profile.dob || null,
      pan_masked_aadhaar:
        panData.maskedAadhaarNumber ||
        panData.maskAadhaarNumber ||
        profile.maskedAadhaar ||
        null,
      dob: profile.dob || null,
      gender: (profile.gender || 'OTHER').toUpperCase(),
      mobile: form.mobile,
      alternate_mobile: form.alternateMobile || null,
      otp: 1234,
      email_personal: form.email,
      email_official: form.officialEmail || null,
      obligations: form.obligations || null,
      state_id: stateId,
      city: cityId && !String(cityId).startsWith('local-') ? cityId : form.city,
      city_id: cityId && !String(cityId).startsWith('local-') ? cityId : null,
      pin: form.pincode,
      pincode: form.pincode,
      address: form.address.trim(),
      term_and_condition: form.notificationConsent ? 'YES' : 'NO',
      ip: null,
      coordinates: null,
      source: 'Fundswall',
      lead_data_source_id: 39,
      utm_source: '',
      utm_campaign: null,
      selfie_image_url: null,
      aadhaar_number: aadhaarNumber,
      aadhaar_last_four: aadhaarNumber.slice(-4),
    };

    try {
      const resp = await saveLendingPage(payload);
      if (isLeadSaveSuccess(resp)) {
        const inserted =
          resp.inserted_id ||
          resp.InsertedId ||
          resp.Data?.inserted_id ||
          resp.data?.inserted_id ||
          resp.lead_id ||
          resp.Data?.lead_id ||
          Date.now();
        setRefId(`FW${inserted}`);
        clearPanCache();
        setSubmitted(true);
        // Success: lock rahe — dubara submit nahi
        return;
      }
      submitLockRef.current = false;
      setSubmitting(false);
      alert(
        String(resp.Message || resp.message || 'Submit failed').replace(/<[^>]+>/g, ' ')
      );
    } catch (err) {
      submitLockRef.current = false;
      setSubmitting(false);
      alert('Submit failed. Please try again.');
    }
  };

  if (submitted) {
    return (
      <main className="apn-page">
        <section className="apn-success-card">
          <div className="apn-confetti" aria-hidden="true">
            {Array.from({ length: 24 }).map((_, i) => (
              <span key={i} className={`apn-confetti-piece p${i % 6}`} />
            ))}
          </div>
          <div className="apn-success-icon"><FaCheckCircle /></div>
          <h1>Application Submitted!</h1>
          <p className="apn-ref">Reference ID: <strong>{refId}</strong></p>
          <ul className="apn-next-steps">
            <li>Our team will review your application shortly.</li>
            <li>Keep your Aadhaar-linked mobile reachable for verification.</li>
            <li>You may receive a call or SMS with next steps.</li>
          </ul>
          <div className="apn-success-actions">
            <Link to="/" className="apn-btn-secondary"><FaHome /> Home</Link>
            <a href="tel:+919211460800" className="apn-btn"><FaPhoneAlt /> Call Us</a>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="apn-page">
      {submitting && (
        <div className="apn-overlay">
          <Spinner animation="border" />
          <p>Submitting your application… Please wait, do not refresh or click again.</p>
        </div>
      )}

      <section className={`apn-card ${otpVerified ? 'apn-card-wide' : ''}`}>
        <aside className="apn-left">
          <div className="apn-left-visual">
            <img className="apn-left-photo" src={applyNowImage} alt="Personal loan" />
          </div>
          <div className="apn-left-content">
            <h2>Personal Loan in minutes</h2>
            <p>Verify identity with PAN &amp; Aadhaar, then complete a short application.</p>
            <ul>
              <li><FaCheck /> Instant KYC verification</li>
              <li><FaCheck /> 100% paperless process</li>
              <li><FaCheck /> Transparent charges</li>
            </ul>
          </div>
        </aside>

        <div className="apn-right">
          <div className="apn-steps">
            <div className={`apn-step ${otpVerified ? 'done' : 'active'}`}>
              <span>{otpVerified ? <FaCheck /> : '1'}</span>
              <div><small>Step 1</small><strong>Identity</strong></div>
            </div>
            <span className={`apn-track ${otpVerified ? 'filled' : ''}`} />
            <div className={`apn-step ${otpVerified ? 'active' : ''}`}>
              <span>2</span>
              <div><small>Step 2</small><strong>Application</strong></div>
            </div>
          </div>

          <h1>{otpVerified ? 'Application details' : 'Verify your identity'}</h1>
          <p className="apn-sub">
            {otpVerified
              ? 'Complete loan and contact details to submit'
              : 'PAN + Aadhaar OTP verification'}
          </p>

          <Form noValidate onSubmit={handleSubmit}>
            {!otpVerified && (
              <>
                <div className="apn-field">
                  <label>PAN Number <em>*</em></label>
                  <div className={`apn-input ${errors.panNumber ? 'has-error' : ''} ${panVerifiedOk ? 'is-ok' : ''}`}>
                    <FaIdCard />
                    <input
                      type="text"
                      maxLength={10}
                      value={panNumber}
                      placeholder="ABCDE1234F"
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                        setPanNumber(v);
                        setOtpSent(false);
                        setOtpVerified(false);
                        setOtp('');
                        setPanVerifiedOk(false);
                        setPanBlacklisted(false);
                        setPanBlocked(false);
                        setPanWarning('');
                        setErrors((er) => ({ ...er, panNumber: '' }));
                      }}
                      onBlur={handlePanBlur}
                      autoComplete="off"
                    />
                    {panChecking && <Spinner size="sm" animation="border" />}
                    {panVerifiedOk && !panChecking && <FaCheckCircle className="ok" />}
                  </div>
                  {errors.panNumber && <div className="apn-error">{errors.panNumber}</div>}
                  {panWarning && !errors.panNumber && (
                    <div
                      className={
                        /re-apply|verified successfully|you can continue/i.test(
                          panWarning
                        )
                          ? 'apn-ok-msg'
                          : 'apn-warn-msg'
                      }
                    >
                      {panWarning}
                    </div>
                  )}
                </div>

                <div className="apn-field">
                  <label>Aadhaar Number <em>*</em></label>
                  <div className={`apn-input ${errors.aadhaarNumber ? 'has-error' : ''}`}>
                    <FaFingerprint />
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={14}
                      value={formatAadhaar(aadhaarNumber)}
                      placeholder="XXXX XXXX XXXX"
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 12);
                        setAadhaarNumber(v);
                        setOtpSent(false);
                        setOtpVerified(false);
                        setOtp('');
                        setErrors((er) => ({ ...er, aadhaarNumber: '' }));
                      }}
                      autoComplete="off"
                    />
                  </div>
                  {errors.aadhaarNumber && <div className="apn-error">{errors.aadhaarNumber}</div>}
                </div>

                {!otpSent && (
                  <Button
                    type="button"
                    className="apn-btn apn-btn-compact"
                    disabled={otpBusy || panBlacklisted}
                    onClick={handleSendOtp}
                  >
                    {otpBusy ? 'Sending…' : <>Send OTP <FaArrowRight /></>}
                  </Button>
                )}

                {otpSent && (
                  <div className="apn-otp-box">
                    <div className="apn-info"><FaShieldAlt /> OTP sent to Aadhaar-linked mobile</div>
                    <div className="apn-field">
                      <label>Enter OTP <em>*</em></label>
                      <div className={`apn-input ${errors.otp ? 'has-error' : ''}`}>
                        <FaLock />
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={otp}
                          placeholder="6-digit OTP"
                          onChange={(e) => {
                            setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                            setErrors((er) => ({ ...er, otp: '' }));
                          }}
                        />
                      </div>
                      {errors.otp && <div className="apn-error">{errors.otp}</div>}
                    </div>
                    <Button
                      type="button"
                      className="apn-btn apn-btn-compact"
                      disabled={otpBusy}
                      onClick={handleVerifyOtp}
                    >
                      {otpBusy ? 'Verifying…' : <>Verify OTP <FaArrowRight /></>}
                    </Button>
                    <button
                      type="button"
                      className="apn-resend"
                      disabled={resendLeft > 0 || otpBusy}
                      onClick={handleSendOtp}
                    >
                      {resendLeft > 0
                        ? `Resend OTP in ${resendLeft}s`
                        : "Didn't receive OTP? Resend"}
                    </button>
                  </div>
                )}
              </>
            )}

            {otpVerified && (
              <div className="apn-step2">
                <div className="apn-verified-banner">
                  <FaCheckCircle /> Identity verified — name, DOB &amp; gender saved securely
                </div>

                <div className="apn-grid apn-step2-fields">
                  <div className="apn-field">
                    <label>Monthly Income <em>*</em></label>
                    <input
                      className="apn-plain"
                      type="number"
                      value={form.monthlyIncome}
                      onChange={(e) => updateFormField('monthlyIncome', e.target.value)}
                      placeholder="Min ₹50,000"
                    />
                    {errors.monthlyIncome && <div className="apn-error">{errors.monthlyIncome}</div>}
                  </div>
                  <div className="apn-field">
                    <label>Loan Amount <em>*</em></label>
                    <input
                      className="apn-plain"
                      type="number"
                      value={form.loanAmount}
                      onChange={(e) => updateFormField('loanAmount', e.target.value)}
                      placeholder="₹10,000 – ₹1,00,000"
                    />
                    {errors.loanAmount && <div className="apn-error">{errors.loanAmount}</div>}
                  </div>
                  <div className="apn-field">
                    <label>Mobile <em>*</em></label>
                    <input
                      className="apn-plain"
                      type="tel"
                      maxLength={10}
                      value={form.mobile}
                      onChange={(e) =>
                        updateFormField(
                          'mobile',
                          e.target.value.replace(/\D/g, '').slice(0, 10)
                        )
                      }
                    />
                    {errors.mobile && <div className="apn-error">{errors.mobile}</div>}
                  </div>
                  <div className="apn-field">
                    <label>Alternate Mobile</label>
                    <input
                      className="apn-plain"
                      type="tel"
                      maxLength={10}
                      value={form.alternateMobile}
                      onChange={(e) =>
                        updateFormField(
                          'alternateMobile',
                          e.target.value.replace(/\D/g, '').slice(0, 10)
                        )
                      }
                    />
                    {errors.alternateMobile && (
                      <div className="apn-error">{errors.alternateMobile}</div>
                    )}
                  </div>
                  <div className="apn-field">
                    <label>Personal Email <em>*</em></label>
                    <input
                      className="apn-plain"
                      type="email"
                      value={form.email}
                      onChange={(e) => updateFormField('email', e.target.value)}
                    />
                    {errors.email && <div className="apn-error">{errors.email}</div>}
                  </div>
                  <div className="apn-field">
                    <label>Official Email</label>
                    <input
                      className="apn-plain"
                      type="email"
                      value={form.officialEmail}
                      onChange={(e) => updateFormField('officialEmail', e.target.value)}
                    />
                    {errors.officialEmail && (
                      <div className="apn-error">{errors.officialEmail}</div>
                    )}
                  </div>
                  <div className="apn-field">
                    <label>Obligations</label>
                    <input
                      className="apn-plain"
                      type="number"
                      value={form.obligations}
                      onChange={(e) => updateFormField('obligations', e.target.value)}
                    />
                    {errors.obligations && <div className="apn-error">{errors.obligations}</div>}
                  </div>

                  <SearchableSelect
                    label="State"
                    required
                    value={stateId}
                    displayValue={form.state}
                    options={states}
                    placeholder="Search state"
                    error={errors.state}
                    getOptionLabel={(o) => o.state}
                    getOptionValue={(o) => o.state_id}
                    onSelect={handleStateSelect}
                  />
                  <SearchableSelect
                    label="City"
                    required
                    value={cityId}
                    displayValue={form.city}
                    options={cities}
                    placeholder="Search city"
                    disabled={!stateId}
                    error={errors.city}
                    getOptionLabel={(o) => o.city}
                    getOptionValue={(o) => o.city_id}
                    onSelect={handleCitySelect}
                  />
                  <SearchableSelect
                    label="Pincode"
                    required
                    value={form.pincode}
                    displayValue={form.pincode}
                    options={
                      pincodes.length
                        ? pincodes
                        : form.pincode
                          ? [{ m_pincode_id: form.pincode, m_pincode_value: form.pincode }]
                          : []
                    }
                    placeholder={pincodes.length ? 'Search pincode' : 'Type 6-digit pincode'}
                    disabled={!form.city}
                    error={errors.pincode}
                    getOptionLabel={(o) => o.m_pincode_value || o}
                    getOptionValue={(o) => o.m_pincode_value || o}
                    onSelect={(o) => {
                      const pin = o.m_pincode_value || String(o);
                      updateFormField('pincode', pin);
                    }}
                  />
                </div>

                {!pincodes.length && form.city && (
                  <div className="apn-field">
                    <label>Enter Pincode <em>*</em></label>
                    <input
                      className="apn-plain"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={form.pincode}
                      onChange={(e) =>
                        updateFormField(
                          'pincode',
                          e.target.value.replace(/\D/g, '').slice(0, 6)
                        )
                      }
                      placeholder="6-digit pincode"
                    />
                    {errors.pincode && <div className="apn-error">{errors.pincode}</div>}
                  </div>
                )}

                <div className="apn-field">
                  <label>Address <em>*</em></label>
                  <textarea
                    className="apn-plain apn-textarea"
                    rows={2}
                    maxLength={MAX_ADDRESS_LENGTH}
                    value={form.address}
                    onChange={(e) => updateFormField('address', e.target.value)}
                    placeholder="Full residential address (min 15 characters)"
                  />
                  <div className="apn-hint">
                    {form.address.length}/{MAX_ADDRESS_LENGTH}
                  </div>
                  {errors.address && <div className="apn-error">{errors.address}</div>}
                </div>

                <div className="apn-consent-block">
                  <p className="apn-declaration">
                    I hereby declare that all the above information provided by me is
                    true, correct, and verified to the best of my knowledge.
                    <em> *</em>
                  </p>

                  <label className="apn-check">
                    <input
                      type="checkbox"
                      checked={form.notificationConsent}
                      onChange={(e) =>
                        updateFormField('notificationConsent', e.target.checked)
                      }
                    />
                    <span>
                      I hereby authorise to send notifications via SMS, Email, RCS and
                      others as per{' '}
                      <a href="/terms-conditions" target="_blank" rel="noreferrer">
                        Terms of Service
                      </a>
                      /
                      <a href="/privacy-policy" target="_blank" rel="noreferrer">
                        Privacy Policy
                      </a>
                      <em> *</em>
                    </span>
                  </label>
                  {errors.notificationConsent && (
                    <div className="apn-error">{errors.notificationConsent}</div>
                  )}
                </div>

                <Button
                  type="submit"
                  className="apn-btn"
                  disabled={submitting || submitted || panBlacklisted}
                  onClick={(e) => {
                    if (submitLockRef.current || submitting || submitted) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  {submitting ? 'Submitting…' : <>Submit Application <FaArrowRight /></>}
                </Button>
              </div>
            )}
          </Form>

          <div className="apn-trust">
            <span><FaLock /> 256-bit encryption</span>
            <span>No paperwork</span>
            <span>No hidden charges</span>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ApplyNew;
